<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useApiClient, useI18n, useQuery, useRoute } from '@fusapp-work/shell'
import { useFrontendVersion } from '../../composables/useFrontendVersion'
import { useConsolePrincipal } from '../../features/organization/personnel/useConsolePrincipal'
import { createOrganizationApi } from '../../features/organization/shared/organizationApi'
import { useConsoleNavigation } from './useConsoleNavigation'
import { useConsoleCachedFacts } from './useConsoleCachedFacts'
import { useConsoleNavigationCounts } from './useConsoleNavigationCounts'
import ConsolePalette from './ConsolePalette.vue'
import ConsoleAccount from './ConsoleAccount.vue'
import ShellIcon from './ShellIcon.vue'
import ConsoleAppReturn from './ConsoleAppReturn.vue'
import '../../styles/shell/console-shell.css'

const { t, locale } = useI18n()
const route = useRoute()
const { title, description, section, groups } = useConsoleNavigation()
/** Inert measurement markers for the golden-live design-parity harness (no styling, no behaviour). */
const goldenGroup = { organization: 'organizasyon', identity: 'kimlik', communication: 'iletisim', integration: 'entegrasyon', infrastructure: 'altyapi' } as const
/** Console v2.dc.html:2830 `hasCount: d[3] > 0` — an unknown or zero count renders no badge. */
function navCount(key: string): number { return (facts.value.counts as Readonly<Record<string, number | undefined>>)[key] ?? 0 }
const principal = useConsolePrincipal(), organizationApi = createOrganizationApi(useApiClient())
useQuery({ queryKey: computed(() => ['console', principal.value, 'organizations']), queryFn: ({ signal }) => organizationApi.list(signal),
  enabled: computed(() => Boolean(principal.value)),
  retry: false, staleTime: 30_000, refetchOnWindowFocus: false })
const facts = useConsoleCachedFacts()
useConsoleNavigationCounts(principal, computed(() => facts.value.organizationId))
const { sha, isLoading } = useFrontendVersion()
const collapsed = ref(false)
const mobileOpen = ref(false)
const isMobile = ref(false)
const sidebar = ref<HTMLElement>()
let media: MediaQueryList | undefined
function updateViewport() { isMobile.value = media?.matches ?? false; if (!isMobile.value) mobileOpen.value = false }
async function openMobile() { mobileOpen.value = true; await nextTick(); sidebar.value?.querySelector<HTMLButtonElement>('button')?.focus() }
const paletteOpen = ref(false)
const mobileToggle = ref<HTMLButtonElement>()
const paletteReturnFocus = ref<HTMLElement>()
function openSearch(returnFocus?: HTMLElement) { paletteReturnFocus.value = returnFocus; paletteOpen.value = true }
async function closeMobile() { mobileOpen.value = false; await nextTick(); mobileToggle.value?.focus() }
function keyboard(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); openSearch() }
  if (event.key === 'Tab' && mobileOpen.value) {
    const controls = Array.from(sidebar.value?.querySelectorAll<HTMLElement>('a[href],button:not([disabled])') ?? []).filter(item => item.getClientRects().length)
    const first = controls[0]; const last = controls.at(-1)
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }
  if (event.key === 'Escape' && mobileOpen.value) closeMobile()
}
watch(() => route.path, () => {
  const wasOpen = mobileOpen.value
  mobileOpen.value = false; paletteOpen.value = false
  if (wasOpen) void nextTick(() => document.getElementById('console-content')?.focus())
})
onMounted(() => {
  media = window.matchMedia('(max-width: 899px)'); updateViewport(); media.addEventListener('change', updateViewport)
  document.addEventListener('keydown', keyboard)
})
onUnmounted(() => { document.removeEventListener('keydown', keyboard); media?.removeEventListener('change', updateViewport) })
</script>

<template>
  <div
    class="fw-app-shell console-shell"
    :class="{ 'console-shell--collapsed': collapsed, 'console-shell--mobile-open': mobileOpen }"
    :style="{ '--console-company-accent': facts.accent }"
    data-surface="console"
    :lang="locale"
  >
    <a
      class="console-skip"
      href="#console-content"
    >{{ t('consoleShell.skip') }}</a>
    <button
      v-if="mobileOpen"
      class="console-scrim"
      :aria-label="t('consoleShell.close')"
      @click="closeMobile"
    />
    <aside
      id="console-navigation"
      ref="sidebar"
      :inert="isMobile && !mobileOpen"
      class="console-sidebar"
    >
      <div class="console-brand">
        <span
          class="console-brand-mark"
          data-golden-region="logo-mark"
        ><img
          src="/brand/logo-mark-cobalt.svg"
          alt="Fusapp"
        ></span><div
          class="console-brand-label"
          data-golden-region="brand-text"
        >
          <strong>{{ t('consoleShell.surface') }}</strong><small :title="facts.organizationName">{{ facts.organizationName ? `${facts.organizationName} · ${t('consoleShell.group')}` : t('consoleShell.scope') }}</small>
        </div><button
          class="console-mobile-close console-icon-button"
          :aria-label="t('consoleShell.close')"
          @click="closeMobile"
        >
          <ShellIcon name="close" />
        </button>
        <button
          class="console-collapse"
          data-testid="shell-collapse"
          :aria-label="t(collapsed ? 'consoleShell.expand' : 'consoleShell.collapse')"
          :aria-expanded="!collapsed"
          aria-controls="console-navigation"
          @click="collapsed = !collapsed"
        >
          <ShellIcon name="collapse" /><span>{{ t('consoleShell.collapse') }}</span>
        </button>
      </div>
      <nav :aria-label="t('consoleShell.navigation')">
        <template
          v-for="group in groups"
          :key="group.key"
        >
          <h2 :data-golden-region="`nav-${goldenGroup[group.key]}`">
            {{ group.label }}
          </h2><router-link
            v-for="item in group.items"
            :key="item.key"
            :to="item.to"
            :data-nav="item.key"
            :data-golden-region="`nav-${goldenGroup[group.key]}`"
            :title="item.label"
            :class="{ 'console-nav-active': section === item.key }"
            :aria-current="section === item.key ? 'page' : undefined"
          >
            <ShellIcon :name="item.key" /><span data-golden="nav-label">{{ item.label }}</span><span
              v-if="navCount(item.key) > 0"
              class="console-nav-count"
              data-golden="nav-badge"
              :class="{ 'console-nav-count--channels': item.key === 'channels' }"
              :title="t(item.key === 'users' ? 'consoleShell.observedCount' : 'consoleShell.loadedCount')"
            >{{ navCount(item.key) }}</span>
          </router-link>
        </template>
      </nav>
      <footer class="console-sidebar-footer">
        <ConsoleAppReturn />
        <button
          class="console-search-button"
          data-golden-region="sidebar-footer-search"
          :aria-label="t('consoleShell.search')"
          @click="openSearch()"
        >
          <ShellIcon name="search" /><span>{{ t('consoleShell.searchButton') }}</span><kbd>⌘K</kbd>
        </button>
      </footer>
    </aside>
    <div
      class="console-workspace"
      :inert="isMobile && mobileOpen"
    >
      <header class="console-header">
        <button
          ref="mobileToggle"
          class="console-mobile-toggle console-icon-button"
          :aria-label="t('consoleShell.menu')"
          :aria-expanded="mobileOpen"
          aria-controls="console-navigation"
          @click="openMobile"
        >
          <ShellIcon name="menu" />
        </button><div
          class="console-heading"
          data-golden-region="topbar-title"
        >
          <h1>{{ title }}</h1><div class="console-heading-sub">
            {{ description }}
          </div>
        </div><ConsoleAccount
          :sha="sha"
          :version-loading="isLoading"
          :organization-name="facts.organizationName"
          @search="openSearch"
        />
      </header><div
        class="console-accent"
        data-golden-region="topbar-accent"
      /><main
        id="console-content"
        class="console-content"
        tabindex="-1"
      >
        <slot />
      </main>
    </div>
    <ConsolePalette
      v-if="paletteOpen"
      :return-focus="paletteReturnFocus"
      @close="paletteOpen = false"
    />
  </div>
</template>
